import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { trackCreate, useOpenedGoal } from "@/lib/metrica";
import { useSetupReturn } from "@/lib/setupFlow";
import { MOBILE_STICKY_FOOTER } from "@/lib/utils";
import { fmtTRY, CUSTOMER_STATUS_LABEL } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, Pin } from "lucide-react";
import { toast } from "sonner";
import { StickyPageHead } from "@/components/Common";

const EMPTY = {
  name: "", contact_person: "", phone: "", email: "",
  category: "Diğer", notes: "", status: "active",
};
const PAGE_LIMIT = 25;

export default function Vendors() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {
        page: opts.page ?? page,
        limit: PAGE_LIMIT,
        ...(search ? { search } : {}),
        ...(catFilter !== "all" ? { category: catFilter } : {}),
      };
      const { data } = await api.get("/vendors", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tedarikçi listesi yüklenemedi");
    } finally { setLoading(false); }
  }, [page, search, catFilter]);

  // Debounce search input → single request 300ms after last keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Categories are only for the form dropdowns — separate, cheap call.
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Dashboard FAB'dan ``?new=1`` ile geldiyse Yeni Tedarikçi dialog'unu
  // aç, URL'i temizle.
  useSetupReturn("supplier", open);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setEditing(null); setForm(EMPTY); setOpen(true);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const activeCategories = useMemo(
    () => (categories ?? []).filter((c) => c.status === "active"),
    [categories]
  );
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_LIMIT));

  useOpenedGoal("supplier_create_opened", open && !editing);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/vendors/${editing.id}`, form);
      else await trackCreate("supplier", () => api.post("/vendors", form));
      toast.success(editing ? "Tedarikçi güncellendi" : "Tedarikçi eklendi");
      setOpen(false); setEditing(null); setForm(EMPTY);
      load({ page: editing ? page : 1 });
      if (!editing) setPage(1);
    } catch { toast.error("İşlem başarısız"); }
  };
  // The paginated list omits ``notes`` — fetch the full record before editing.
  const onEdit = async (v) => {
    try {
      const { data } = await api.get(`/vendors/${v.id}`);
      setEditing(v); setForm({ ...EMPTY, ...data }); setOpen(true);
    } catch { toast.error("Tedarikçi yüklenemedi"); }
  };
  const onDelete = async (v) => {
    if (!window.confirm(`"${v.name}" silinsin mi?`)) return;
    await api.delete(`/vendors/${v.id}`);
    toast.success("Silindi"); load();
  };

  const togglePin = async (v) => {
    const next = !v.is_pinned;
    // Optimistic: satırı güncelle, backend re-fetch ile is_pinned desc +
    // sort_key desc sıralamayı uygula.  Hata olursa revert.
    setItems((prev) => (prev ?? []).map((x) => x.id === v.id ? { ...x, is_pinned: next } : x));
    try {
      await api.patch(`/vendors/${v.id}/pin`, { is_pinned: next });
      load();
    } catch (_) {
      setItems((prev) => (prev ?? []).map((x) => x.id === v.id ? { ...x, is_pinned: !next } : x));
      toast.error("Sabitleme değiştirilemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <StickyPageHead>
      <PageHeader title="Tedarikçiler" subtitle="Tedarikçi ve gider kartlarınızı yönetin"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg" data-testid="add-vendor-button">
                <Plus className="w-4 h-4 mr-1.5" /> Yeni Tedarikçi
              </Button>
            </DialogTrigger>
            <DialogContent
              className="max-w-xl max-md:top-[max(0.5rem,env(safe-area-inset-top))] max-md:translate-y-0 max-md:max-h-[calc(100dvh_-_1rem_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom))] max-md:overflow-y-auto max-md:data-[state=open]:slide-in-from-top-2 max-md:data-[state=closed]:slide-out-to-top-2"
              data-testid="vendor-dialog">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Tedarikçi Düzenle" : "Yeni Tedarikçi"}</DialogTitle></DialogHeader>
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vendor-form">
                <Field label="Tedarikçi Adı *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="vendor-name-input" />
                <Field label="Yetkili" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} />
                <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="E-posta" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <div className="space-y-1.5">
                  <Label className="text-xs">Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-10 rounded-lg" data-testid="vendor-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(activeCategories ?? []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                      {editing && form.category && !(activeCategories ?? []).some((c) => c.name === form.category) && (
                        <SelectItem value={form.category}>{form.category} (Pasif)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Durum</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktif</SelectItem>
                      <SelectItem value="passive">Pasif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs">Notlar</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                </div>
                <DialogFooter className={`md:col-span-2 ${MOBILE_STICKY_FOOTER}`} data-testid="vendor-form-footer">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="vendor-save-button">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Tedarikçi ara..." className="pl-9 h-10 rounded-lg" data-testid="vendor-search-input" />
        </div>
        <Select value={catFilter} onValueChange={(v) => { setCatFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44 h-10 rounded-lg" data-testid="vendor-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {(categories ?? []).map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.name}{c.status === "passive" ? " (Pasif)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      </StickyPageHead>

      <SectionCard>
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="vendors-loading">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <EmptyState title="Tedarikçi bulunamadı" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/50">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-semibold">Tedarikçi</th>
                    <th className="px-5 py-3 font-semibold">Kategori</th>
                    <th className="px-5 py-3 font-semibold">İletişim</th>
                    <th className="px-5 py-3 font-semibold text-right">Toplam Satın Alma</th>
                    <th className="px-5 py-3 font-semibold text-right">Ödenen</th>
                    <th className="px-5 py-3 font-semibold text-right">Bekleyen Borç</th>
                    <th className="px-5 py-3 font-semibold">Durum</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((v) => (
                    <tr key={v.id} className="hover:bg-slate-50/60 cursor-pointer" data-testid={`vendor-row-${v.id}`} onClick={() => navigate(`/vendors/${v.id}`)}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); togglePin(v); }}
                            className={`shrink-0 p-1 rounded-md transition-colors ${
                              v.is_pinned
                                ? "text-emerald-600 hover:bg-emerald-50"
                                : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"
                            }`}
                            title={v.is_pinned ? "Sabitlemeyi kaldır" : "Sabitle"}
                            aria-label={v.is_pinned ? "Sabitlemeyi kaldır" : "Sabitle"}
                            data-testid={`vendor-pin-${v.id}`}
                          >
                            <Pin className={`w-4 h-4 ${v.is_pinned ? "fill-emerald-600" : ""}`} />
                          </button>
                          <span className="font-medium hover:underline truncate">{v.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-slate-700">{v.category || "—"}</td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        <div>{v.contact_person || "—"}</div>
                        <div className="text-slate-500">{v.phone || v.email || "—"}</div>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(v.total_purchase)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(v.total_paid)}</td>
                      <td className="px-5 py-3 text-right tabular-nums font-semibold">{fmtTRY(v.outstanding_debt)}</td>
                      <td className="px-5 py-3"><StatusBadge value={v.status} label={CUSTOMER_STATUS_LABEL[v.status]} /></td>
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(v)} data-testid={`vendor-edit-${v.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(v)} data-testid={`vendor-delete-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} onPage={setPage} />
          </>
        )}
      </SectionCard>
    </div>
  );
}

function Pagination({ page, totalPages, total, onPage }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-xs text-slate-600" data-testid="vendors-pagination">
      <div>
        Toplam <strong className="text-slate-800">{total}</strong> kayıt · Sayfa <strong>{page}</strong> / {totalPages}
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="vendors-prev-page">
          <ChevronLeft className="w-3.5 h-3.5" /> Önceki
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="vendors-next-page">
          Sonraki <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}
